import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Typography, TextField, Stack, Paper, Chip, CircularProgress, Box, Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import { getCurrentUser } from '../../services/auth';
import ClientHistoryModal from './ClientHistoryModal';

export default function Clients() {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null); // { id, name, phone, status }

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const me = await getCurrentUser();
        const [list, svc] = await Promise.all([
          api.getMyClients(),
          me?.id ? api.getServices(me.id) : Promise.resolve([]),
        ]);
        setClients(list || []);
        setServices(svc || []);
      } catch (e) {
        setError(e.message || t('clients.loadError'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(c =>
      (c.name || '').toLowerCase().includes(q) || (c.phone || '').toLowerCase().includes(q)
    );
  }, [clients, query]);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h5" gutterBottom>{t('clients.title')}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TextField
        fullWidth
        size="small"
        placeholder={t('clients.searchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 2 }}
      />

      {loading ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}><CircularProgress color="primary" /></Box>
      ) : filtered.length === 0 ? (
        <Typography sx={{ color: '#57564d', mt: 2 }}>{t('clients.empty')}</Typography>
      ) : (
        <Stack spacing={1}>
          {filtered.map((c) => (
            <Paper
              key={c.id}
              onClick={() => setSelected(c)}
              sx={{
                p: 1.5, cursor: 'pointer', bgcolor: '#faf7f2',
                border: '1px solid rgba(28,29,23,0.18)',
                transition: '0.15s', '&:hover': { borderColor: '#c9a24a', boxShadow: 2 }
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                     spacing={1} sx={{ mb: 0.75 }}>
                <Typography fontWeight={600}>{c.name || '—'}</Typography>
                <Typography variant="body2" sx={{ color: '#57564d' }}>{c.phone || '—'}</Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                <Chip size="small" variant="outlined" label={`${t('bookingDetails.statTotal')}: ${c.total}`} />
                {c.upcoming > 0 && <Chip size="small" color="info" label={`${t('bookingDetails.statUpcoming')}: ${c.upcoming}`} />}
                {c.cancelled > 0 && <Chip size="small" color="error" label={`${t('bookingDetails.statCancelled')}: ${c.cancelled}`} />}
                {c.noShow > 0 && <Chip size="small" color="warning" label={`${t('bookingDetails.statNoShow')}: ${c.noShow}`} />}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      <ClientHistoryModal
        open={!!selected}
        onClose={() => setSelected(null)}
        user={selected}
        services={services}
      />
    </Container>
  );
}
